import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy } from 'lucide-react';

/**
 * Barre de tonnage animée "ultra wahou" : remplissage dynamique + halo lumineux
 * + éclat quand l'objectif (target) est atteint ou dépassé.
 */
export const TonnageBar = ({ current, target, label, big = false, unit = 'kg' }) => {
  const pct = target ? Math.min(120, Math.round((current / target) * 100)) : Math.min(100, current > 0 ? 30 : 0);
  const reached = target ? current >= target : false;
  const wasReached = useRef(false);
  const [justReached, setJustReached] = useState(false);

  useEffect(() => {
    if (reached && !wasReached.current) {
      setJustReached(true);
      wasReached.current = true;
      const t = setTimeout(() => setJustReached(false), 1400);
      return () => clearTimeout(t);
    }
    if (!reached) wasReached.current = false;
  }, [reached]);

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`${big ? 'text-xs' : 'text-[10px]'} font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1`}>
          {label}
        </span>
        <span className={`${big ? 'text-sm' : 'text-[11px]'} font-mono font-bold ${reached ? 'text-emerald-400' : 'text-white'}`}>
          {Math.round(current)} {unit}{target ? ` / ${Math.round(target)} ${unit}` : ''}
        </span>
      </div>
      <div className={`relative ${big ? 'h-3.5' : 'h-2'} rounded-full bg-white/8 overflow-hidden`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, pct)}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`h-full rounded-full ${reached ? 'bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400' : 'bg-gradient-to-r from-purple-500 to-primary'}`}
          style={reached ? { boxShadow: '0 0 16px rgba(52,211,153,0.7)' } : undefined}
        />
        {pct > 100 && (
          <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(20, pct - 100)}%` }} className="absolute top-0 right-0 h-full bg-yellow-300" style={{ boxShadow: '0 0 12px rgba(250,204,21,0.9)' }} />
        )}
      </div>
      <AnimatePresence>
        {justReached && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 0 }}
            animate={{ opacity: 1, scale: 1.15, y: -6 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute -top-3 right-0 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg shadow-emerald-500/50"
          >
            <Trophy className="w-3 h-3" /> {big ? 'OBJECTIF ATTEINT !' : 'Objectif !'}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TonnageBar;
